import React, { useState } from "react";
import { useParams, useLocation } from "react-router-dom";

import "../styles/useCustomScrollbar";
import UserDetailSection from "./component/User_Details_Section/UserDetailSection";
import TabNavigation from "./component/TabNavigation";
import AllLogsTab from "./component/All_logs/AllLogsTab";
import InsightsTab from "./component/Insights_Tab/InsightsTab";
import RecipeTab from "./component/Recipe_Tab/RecipeTab";
import PageHeader from "../Ui_Components/PageHeader";
import useCustomScrollbar from "../styles/useCustomScrollbar";
import AssignNutritionistTab from "../Sales_Client/AssignNutritionistTab";
import { usePermissions } from "../../hooks/usePermissions";
import { Button } from "../Ui_Components/Button";

const ClientDetailPage: React.FC = () => {
  useCustomScrollbar();
  const { userId } = useParams<{ userId: string }>();
  const location = useLocation();
  const { can } = usePermissions();
  // "Assign nutritionist" tab is editable only when the user has client.assign.
  const canAssignClients = can("client", "assign");
  const initialTab = (location.state as { initialTab?: string } | null)?.initialTab ?? "logs";
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isUserDetailExpanded, setIsUserDetailExpanded] = useState(false);

  if (!userId) {
    return (
      <div className="min-h-screen bg-[#F7F7FA] flex items-center justify-center">
        <div className="text-center">
          <p className="text-base text-red-500 mb-2">No valid user ID found in URL</p>
          <p className="text-xs text-gray-500">Current path: {window.location.pathname}</p>
          <Button
            variant="primary"
            onClick={() => window.history.back()}
            className="mt-4"
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const userIdNumber = parseInt(userId, 10);

  if (isNaN(userIdNumber)) {
    return (
      <div className="min-h-screen bg-[#F7F7FA] flex items-center justify-center">
        <div className="text-center">
          <p className="text-base text-red-500 mb-2">Invalid user ID format</p>
          <p className="text-xs text-gray-500">
            User ID must be a number, received: {userId}
          </p>
          <Button
            variant="primary"
            onClick={() => window.history.back()}
            className="mt-4"
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const isRecipeTab = activeTab === "recipe";

  // ─── Recipe tab: h-screen with internal scroll (PlansPage pattern) ───────────
  if (isRecipeTab) {
    return (
      <div className="h-screen flex flex-col bg-[#F7F7FA] overflow-hidden">
        <div className="flex-shrink-0">
          <PageHeader
            title="Log details"
            breadcrumb="Clients > Log details"
            breadcrumbLinks={["/client", ""]}
          />
        </div>

        <div
          className={`flex-1 flex flex-col ${
            isUserDetailExpanded
              ? "overflow-y-auto custom-scrollbar-overlay scrollbar-visible"
              : "overflow-hidden"
          }`}
        >
          <div
            className="flex flex-col"
            style={
              isUserDetailExpanded
                ? { minHeight: "100%" }
                : { flex: 1, minHeight: 0 }
            }
          >
            <div className="flex-shrink-0">
              <UserDetailSection
                userId={userIdNumber}
                onExpandChange={setIsUserDetailExpanded}
              />
            </div>

            <div className="flex-shrink-0">
              <TabNavigation
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
              />
            </div>

            <div
              className="px-4 sm:px-6 lg:px-8 pb-4"
              style={
                isUserDetailExpanded
                  ? { height: "calc(100vh - 500px)", minHeight: "500px" }
                  : { flex: 1, minHeight: 0 }
              }
            >
              <div className="h-full">
                <RecipeTab />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Logs / Insights tabs: original min-h-full page scroll ───────────────────
  return (
    <div className="min-h-full flex flex-col bg-[#F7F7FA]">
      <PageHeader
        title="Log details"
        breadcrumb="Clients > Log details"
        breadcrumbLinks={["/client", ""]}
      />

      <UserDetailSection userId={userIdNumber} />

      <TabNavigation
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
      />

      <div className="flex-1">
        <div className={activeTab === "logs" ? "block" : "hidden"}>
          <AllLogsTab userId={userIdNumber} selectedDate={selectedDate} />
        </div>

        {activeTab === "insights" && (<InsightsTab userId={userIdNumber} />)}
   {/* {activeTab === "insights" && (<InsightsTab  />)} */}

        {activeTab === "nutritionist" && (
          <AssignNutritionistTab userId={userIdNumber} readOnly={!canAssignClients} />
        )}
      </div>
    </div>
  );
};

export default ClientDetailPage;